import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, LayoutDashboard, BarChart3, Plus, CreditCard, LogOut, Loader2,
  Search, Filter, Eye, Calendar, DollarSign, Target
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdvertiserCampaigns() {
  const navigate = useNavigate();
  const [advertiser, setAdvertiser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (advertiser) fetchCampaigns();
  }, [advertiser, statusFilter]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/me`, { withCredentials: true });
      setAdvertiser(response.data);
    } catch (error) {
      navigate("/advertiser/login");
    }
  };

  const fetchCampaigns = async () => {
    try {
      let url = `${API}/advertiser-portal/campaigns`;
      if (statusFilter !== "all") url += `?status=${statusFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/advertiser-portal/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/advertiser/login");
  };

  const statusColors = {
    draft: "bg-slate-500/20 text-slate-400",
    pending_approval: "bg-amber-500/20 text-amber-400",
    approved: "bg-blue-500/20 text-blue-400",
    active: "bg-emerald-500/20 text-emerald-400",
    paused: "bg-orange-500/20 text-orange-400",
    completed: "bg-purple-500/20 text-purple-400",
    rejected: "bg-red-500/20 text-red-400"
  };

  const campaignTypeLabels = {
    standard: "Standard",
    takeover_full: "Full Takeover",
    takeover_wrapper: "Branded Wrapper",
    takeover_timeslot: "Time-slot"
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="advertiser-campaigns">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ParkWise
                </span>
                <span className="hidden sm:inline text-slate-400 text-sm ml-2">Advertiser Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-400 hidden sm:block">
                {advertiser?.company_name}
              </span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-slate-900/50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2">
            <Link to="/advertiser" className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white text-sm font-medium whitespace-nowrap">
              <LayoutDashboard className="w-4 h-4 inline mr-2" />Dashboard
            </Link>
            <Link to="/advertiser/campaigns" className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Campaigns
            </Link>
            <Link to="/advertiser/billing" className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white text-sm font-medium whitespace-nowrap">
              <CreditCard className="w-4 h-4 inline mr-2" />Billing
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Campaigns
            </h1>
            <p className="text-slate-400 text-sm mt-1">Manage and track your advertising campaigns</p>
          </div>
          <Button 
            onClick={() => navigate("/advertiser/campaigns/new")} 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-campaign-btn"
          >
            <Plus className="w-4 h-4 mr-2" />New Campaign
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">All Campaigns</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : campaigns.length > 0 ? (
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <Card 
                key={campaign.campaign_id} 
                className="bg-slate-800/50 border-slate-700 cursor-pointer hover:bg-slate-800 transition-colors"
                onClick={() => navigate(`/advertiser/campaigns/${campaign.campaign_id}`)}
                data-testid={`campaign-${campaign.campaign_id}`}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-white">{campaign.name}</h3>
                        <Badge className={statusColors[campaign.status]}>
                          {campaign.status?.replace("_", " ")}
                        </Badge>
                        <Badge variant="outline" className="text-slate-400 border-slate-600">
                          {campaignTypeLabels[campaign.campaign_type] || campaign.campaign_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-400 line-clamp-1">{campaign.description || "No description"}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center sm:text-right">
                      <div>
                        <p className="text-xs text-slate-500">Impressions</p>
                        <p className="text-sm font-medium text-white">{(campaign.metrics?.impressions_delivered || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Spend</p>
                        <p className="text-sm font-medium text-white">£{(campaign.metrics?.spend_to_date || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Period</p>
                        <p className="text-sm font-medium text-white">{campaign.schedule?.start_date?.slice(5)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <BarChart3 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No campaigns yet</h3>
            <p className="text-slate-400 mb-6">Create your first campaign to start reaching customers</p>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate("/advertiser/campaigns/new")}
            >
              <Plus className="w-4 h-4 mr-2" />Create Campaign
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
